import React from 'react';
import styled from 'styled-components';
import { ReactComponent as DownloadIcon } from '../../images/Download.svg';
import { blue_green, blue_green_hover, white, sawatch_blue, light_gray } from '../../styles/emit-styles/ColorScheme';
import { MEDIUM_RADIUS, TRANSITION_LENGTH } from '../../styles/emit-styles/CardStyles';
import { buildDownloadFilename, formatData, formatTimestamp } from './UtilityFunctions';
import exceljs from 'exceljs';
import FileSaver from 'file-saver';
import { logoBase64 } from './Logo';
import { AVOIDED_TABLE_TYPE } from './HelperConstants';
import { DateTime } from 'luxon';

const DOWNLOAD_BUTTON_COLOR = blue_green;
const HEADER_COLOR = sawatch_blue.slice(1)

const DownloadContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding-left: 4px;
    padding-right: 8px;
    background-color: ${({ disabled }) => disabled ? light_gray : DOWNLOAD_BUTTON_COLOR}; ;
    color: ${white};
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    font-weight: 500;
    height: ${({ height }) => height || '32px'};
    width: ${({ width }) => width || '128px'}; 
    border-radius: ${MEDIUM_RADIUS}px;
    transition: background-color ${TRANSITION_LENGTH} ease;
    &:hover{
      background-color: ${({ disabled }) => !disabled && blue_green_hover};
    }
    font-size: 16px;
`;

const IconWrapper = styled.div`
  fill: ${white};
  height: 20px;
  width: 20px;
`;

const ExcelDownloadButton = ({ width, height, label, dbDisplayName, beginDate, endDate, downloadType, vehicleClasses, selectedVehicleClasses, tableData, cols, methodology, selectedGroups, userSettings, disabled}) => {
    const formattedData = formatExcelData(beginDate, endDate, downloadType, vehicleClasses, selectedVehicleClasses, dbDisplayName, tableData, cols, methodology, selectedGroups, userSettings);
    const filename = buildDownloadFilename(dbDisplayName, downloadType, userSettings);
    return (
        <DownloadContainer
            id={'excel-download-button'}
            onClick={() => !disabled && execExcelDownload(formattedData, filename)}
            target="_blank"
            width={width}
            height={height}
            disabled={disabled}
        >
            <IconWrapper>
                <DownloadIcon />
            </IconWrapper>
            <div>{label}</div>
        </DownloadContainer>
    )
};

export default ExcelDownloadButton;


async function saveFile(workbook, filename) {
    const buf = await workbook.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buf]), filename);
}

function adjustColumnWidth(worksheet) {
    worksheet.columns.forEach(column => {
        const lengths = column.values.map(v => v.toString().length);
        const maxLength = Math.max(...lengths.filter(v => typeof v === 'number'));
        column.width = maxLength + 2;
    });
}

function createExcelDoc(formattedData) {
    const workbook = new exceljs.Workbook();
    const worksheet = workbook.addWorksheet('sheet1', { views: [{ showGridLines: false }] });
    const imgId = workbook.addImage({
        base64: logoBase64,
        extension: 'png'
    });
    worksheet.addRows(formattedData.vehicle);

    // Set style of title block headers
    worksheet.getCell('A1').font = {
        bold: true,
        size: 14
    };
    worksheet.getCell('A2').font = {
        bold: true
    };
    worksheet.getCell(`A${formattedData.headerCount - 2}`).value = {
        text: 'sawatchlabs.com',
        hyperlink: 'http://www.sawatchlabs.com'
    };
    worksheet.getCell(`A${formattedData.headerCount - 2}`).font = {
        underline: true,
        color: { argb: HEADER_COLOR }
    }

    // Set font and fill for table headers
    worksheet.getRow(formattedData.headerCount).font = { bold: true, color: { argb: "FFFFFF" } };
    worksheet.getRow(formattedData.headerCount).eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_COLOR } }
    });

    adjustColumnWidth(worksheet);

    // Add image to cell B2 and preserve size
    worksheet.addImage(imgId, {
        tl: { col: 1, row: 1 },
        ext: { width: 241, height: 132 },
        editAs: 'absolute'
    });

    // Add borders to and format cells
    worksheet.eachRow((row, rowNum) => {
        if (rowNum >= formattedData.headerCount) {
            row.eachCell({ includeEmpty: true }, (cell) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
                cell.alignment = { horizontal: 'left' };
            });
            row.commit();
        }
    });
   
    //We want to right align all columns that include number values
    //For this product it's easier to list the columns we don't want right aligned vs listing out every column we do want right aligned
    let colsToAvoidRightAlign = ["Asset ID", "VIN", "Make", "Model", "Vehicle Class", "Fuel Type"]
    const headerRow = worksheet.getRow(formattedData['headerCount']);
    headerRow.values.forEach((col) => {
        if(!colsToAvoidRightAlign.includes(col)) {
            let idx = headerRow.values.indexOf(col)
            worksheet.getColumn(idx).eachCell((cell) => {
                worksheet.getColumn(idx).alignment = { horizontal: 'right' }
                let rawValue = cell.value;
                // Convert if value is a string that may represent a number
                if (typeof rawValue === 'string') {
                    let cleaned = rawValue.replace(/[,%$]/g, ''); //Remove [commas, percent symbols, dollar sign] symbols from number
                    let number = Number(cleaned);
                    if (!isNaN(number)) {
                        cell.value = number;
                    }
                }
                // Apply number formatting if value is a number
                if (typeof cell.value === 'number') {
                    if (col === 'Fuel Savings') {
                        cell.numFmt = '$#,##0';
                    } else if (cell.value % 1 === 0) {
                        // Format any whole numbers
                        if (col !== 'Year') {
                            // Don't add formatting to year
                            cell.numFmt = '#,##0';
                        }
                    } else {
                        cell.numFmt = '#,##0.00';
                    }
                }
          })
        }
    })
    return workbook;
}

function execExcelDownload(formattedData, filename) {
    const workbook = createExcelDoc(formattedData);
    saveFile(workbook, filename);
}

function formatExcelData(beginDate, endDate, downloadType, vehicleClasses, selectedVehicleClasses, dbDisplayName, tableData, cols, methodology, selectedGroups, userSettings) {
    const csvHeaders = cols.map((c) => {
        let columnLabel = c.excelHeader ?? c.Header;
        return (
            { label: columnLabel, key: c.accessor }
        )
    })

    let selectedClasses = (selectedVehicleClasses?.length === vehicleClasses?.length) ? 'All Classes' : selectedVehicleClasses.join(", ");
    let groupList = selectedGroups.map((item) => item.name)

    let emptyRow = [''];
    let headers = [];
    let accessors = [];
    let reportName = [];
    if (downloadType === AVOIDED_TABLE_TYPE) {
        reportName.push('Avoided Emissions Report - ' + dbDisplayName);
    } else {
        reportName.push('Emissions Report - ' + dbDisplayName);
    }

    const formattedDates = {
        begin: beginDate ? formatTimestamp(userSettings, DateTime.fromJSDate(beginDate).toUTC()).date : '-',
        end: endDate ? formatTimestamp(userSettings, DateTime.fromJSDate(endDate).toUTC()).date : '-'
    }
    let rowArray = [
        ['Sawatch Labs - Emit'],
        reportName,
        ['Download Date: ' + formatTimestamp(userSettings, DateTime.local()).date],
        [`Date Range: ${formattedDates.begin} - ${formattedDates.end}`],
        [`Methodology: ${methodology?.label}`],
        [`Groups: ${groupList.join(", ")}`],
        [`Vehicle Classes: ${selectedClasses}`],
        emptyRow,
        emptyRow,
        headers,
    ]

    if (csvHeaders) {
        csvHeaders.map(item => {
            headers.push(item.label)
            accessors.push(item.key)
            return null;
        })
    }

    const headerCount = rowArray.length;
    if (tableData.length < 1) {
        rowArray.push(['No vehicle to display'])
    } else {
        tableData.forEach(vehicle => {
            let row = []
            accessors.forEach(type => {

                let val = formatData(type, vehicle[type], userSettings, true, true);
                // We should always try to provide a number to excel rather than a string. That's why the rawValues flag above is true.
                // If val can't be converted to number just push the string
                if (isNaN(Number(val))) {
                    row.push(val);
                }
                // Otherwise convert string to number type for excel
                else {
                    row.push(Number(val));
                }

            });
            rowArray.push(row);
        })
    }
    return { "vehicle": rowArray, "headerCount": headerCount };
}